<template>
  <div class="page-view">
    <div class="page-toolbar">
      <el-button type="primary" @click="addSubscription()">Add</el-button>
      <el-input
        v-model="searchQuery"
        class="page-search"
        placeholder="搜索 用户 / 来源 / 关键字"
        clearable
        size="small"
      />
      <el-pagination
        v-model:current-page="pageNum"
        v-model:page-size="pageSize"
        :total="filteredData.length"
        :page-sizes="[20, 50, 100]"
        :layout="paginationLayout"
        :small="isMobile"
        :pager-count="isMobile ? 5 : 7"
      />
    </div>
    <div class="table-wrap" :style="tableWrapStyle">
      <el-auto-resizer>
        <template #default="{ height, width }">
          <el-table-v2 :columns="columns" :data="paginatedData" :width="width" :height="height" fixed />
        </template>
      </el-auto-resizer>
    </div>
  </div>
  <el-dialog v-model="detailDialogVisible" title="Add Subscription" width="500px" :close-on-click-modal="false"
    :close-on-press-escape="false" :show-close="false" :align-center="true">
    <SubscriptionDetail v-model:subscription="subscription"></SubscriptionDetail>
    <template #footer>
      <span class="dialog-footer">
        <el-button :disabled="saving || deleting" @click="detailDialogVisible = false">取消</el-button>
        <el-button type="danger" :loading="deleting" :disabled="saving || !subscription?._id" @click="del()">删除</el-button>
        <el-button type="primary" :loading="saving" :disabled="deleting" @click="save()">保存</el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script setup lang="tsx">
import { computed, ref, watch } from "vue";
import { getSubscriptions, saveSubscription, deleteSubscription, getWechatAccounts, getWechatUser } from "../api/http";
import { ReplyTypesDict, type SubscriptionRecord, type WechatAccountDetail } from "../api/models";
import { messageTypesDict } from "../api/util";
import { ElButton, ElMessage, ElMessageBox } from "element-plus";
import SubscriptionDetail from "../components/SubscriptionDetail.vue";
import { useBreakpoint } from "../composables/useBreakpoint";

const { isMobile } = useBreakpoint();

const subscription = ref<SubscriptionRecord | null>(null);
const detailDialogVisible = ref(false);
const saving = ref(false);
const deleting = ref(false);
const data = ref<SubscriptionRecord[]>([]);

const searchQuery = ref("");

const filteredData = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return data.value;
  return data.value.filter((s) => {
    if ((s.wechatUserId ?? "").toLowerCase().includes(q)) return true;
    if (wechatUserName(s.wechatUserId ?? "").toLowerCase().includes(q)) return true;
    if ((s.keyword ?? "").toLowerCase().includes(q)) return true;
    if ((s.content ?? "").toLowerCase().includes(q)) return true;
    if (s.fromUsers && s.fromUsers.some((id) =>
      id.toLowerCase().includes(q) || friendName(s.wechatUserId ?? "", id).toLowerCase().includes(q)
    )) return true;
    if (s.fromGroups && s.fromGroups.some((id) =>
      id.toLowerCase().includes(q) || groupName(s.wechatUserId ?? "", id).toLowerCase().includes(q)
    )) return true;
    return false;
  });
});

const pageNum = ref(1);
const pageSize = ref(20);
const paginatedData = computed(() => {
  const start = (pageNum.value - 1) * pageSize.value;
  return filteredData.value.slice(start, start + pageSize.value);
});
const paginationLayout = computed(() => (isMobile.value ? "prev, pager, next" : "total, sizes, prev, pager, next, jumper"));

const tableWrapStyle = computed(() =>
  isMobile.value
    ? { height: 50 + Math.max(1, paginatedData.value.length) * 50 + "px", flex: "none", minHeight: "0" }
    : undefined
);

watch(searchQuery, () => { pageNum.value = 1; });

const replyTypes: Array<{ value: number; label: string }> = [];
ReplyTypesDict.map((item: string, index: number) => {
  replyTypes.push({ value: index, label: item });
});

// id → display-name lookups built up after the supporting endpoints
// resolve. Reactive so cellRenderers re-render once the maps fill.
const wechatAccountNames = ref<Record<string, string>>({});
const wechatUserDetails = ref<Record<string, WechatAccountDetail>>({});
const inFlightDetails = new Set<string>();

const wechatUserName = (wcId: string) => wechatAccountNames.value[wcId] || wcId;

const friendName = (ownerWcId: string, contactId: string) => {
  const detail = wechatUserDetails.value[ownerWcId];
  if (!detail) return contactId;
  const f = detail.friends?.find((x) => x.v1 === contactId);
  return f?.nickName || contactId;
};

const groupName = (ownerWcId: string, groupId: string) => {
  const detail = wechatUserDetails.value[ownerWcId];
  if (!detail) return groupId;
  const g = detail.chatrooms?.find((x) => x.v1 === groupId);
  return g?.nickName || groupId;
};

const ensureUserDetail = (wechatUserId: string) => {
  if (!wechatUserId) return;
  if (wechatUserDetails.value[wechatUserId]) return;
  if (inFlightDetails.has(wechatUserId)) return;
  inFlightDetails.add(wechatUserId);
  getWechatUser(wechatUserId)
    .then((res) => {
      if (res.successful) {
        wechatUserDetails.value = { ...wechatUserDetails.value, [wechatUserId]: res.result };
      }
    })
    .finally(() => {
      inFlightDetails.delete(wechatUserId);
    });
};

const loadWechatAccountNames = () => {
  getWechatAccounts().then((res) => {
    if (res.successful) {
      const map: Record<string, string> = {};
      res.result.forEach((a) => {
        map[a.wcId] = a.nickName || a.wAccount || a.wcId;
      });
      wechatAccountNames.value = map;
    }
  });
};

const messageTypesRenderer = ({ cellData: messageTypes }: { cellData: Array<string> }) => (
  <>{getMessageTypes(messageTypes)}</>
);

const replyTypeRenderer = ({ cellData: replyType }: { cellData: number }) => (
  <>{ReplyTypesDict[replyType]}</>
);

const detailCellRenderer = ({ rowData: row }: { rowData: any }) => (
  <>
    <ElButton size="small" onClick={() => { detail(row); }}>详情</ElButton>
  </>
);

const wechatUserRenderer = ({ cellData: wcId }: { cellData: string }) => (
  <span title={wcId}>{wechatUserName(wcId)}</span>
);

const fromUsersRenderer = ({ cellData, rowData }: { cellData: Array<string>; rowData: any }) => {
  if (!cellData || cellData.length === 0) return <span class="hint">全部</span>;
  const names = cellData.map((id) => friendName(rowData.wechatUserId, id));
  return <span title={names.join(", ")}>{names.join(", ")}</span>;
};

const fromGroupsRenderer = ({ cellData, rowData }: { cellData: Array<string>; rowData: any }) => {
  if (!cellData || cellData.length === 0) return <span class="hint">全部</span>;
  const names = cellData.map((id) => groupName(rowData.wechatUserId, id));
  return <span title={names.join(", ")}>{names.join(", ")}</span>;
};

const desktopColumns = [
  { key: "order", dataKey: "order", title: "#", width: 60, align: "center" },
  { key: "wechatUserId", dataKey: "wechatUserId", title: "Wechat User", width: 160, cellRenderer: wechatUserRenderer },
  { key: "messageTypes", dataKey: "messageTypes", title: "Message Types", width: 200, cellRenderer: messageTypesRenderer },
  { key: "fromUsers", dataKey: "fromUsers", title: "From Users", width: 180, cellRenderer: fromUsersRenderer },
  { key: "fromGroups", dataKey: "fromGroups", title: "From Groups", width: 180, cellRenderer: fromGroupsRenderer },
  { key: "keyword", dataKey: "keyword", title: "Keyword", width: 160, flexGrow: 1 },
  { key: "at", dataKey: "at", title: "@", width: 70 },
  { key: "replyType", dataKey: "replyType", title: "Reply Type", width: 110, cellRenderer: replyTypeRenderer },
  { key: "detail", dataKey: "_id", title: "Detail", width: 100, align: "center", fixed: "right" as const, cellRenderer: detailCellRenderer },
];

const mobileColumns = [
  { key: "wechatUserId", dataKey: "wechatUserId", title: "Wechat User", width: 100, flexGrow: 1, cellRenderer: wechatUserRenderer },
  { key: "replyType", dataKey: "replyType", title: "Reply", width: 80, cellRenderer: replyTypeRenderer },
  { key: "detail", dataKey: "_id", title: "", width: 70, align: "center", fixed: "right" as const, cellRenderer: detailCellRenderer },
];

const columns = computed(() => (isMobile.value ? mobileColumns : desktopColumns));

const loadSubscription = () => {
  getSubscriptions().then(res => {
    if (res.successful) {
      res.result.forEach((item, index) => {
        item.order = index + 1;
      });
      data.value = res.result;
      // Pull in friend / chatroom names for each unique owner so the
      // fromUsers / fromGroups columns can show display names.
      const owners = new Set<string>();
      res.result.forEach((s) => {
        if (s.wechatUserId) owners.add(s.wechatUserId);
      });
      owners.forEach((id) => ensureUserDetail(id));
    } else {
      ElMessage({
        message: res.message,
        type: "error",
      });
    }
  });
}

const getMessageTypes = (messageTypes: Array<string>) => {
  const data = new Array<string>();
  if (messageTypes) {
    messageTypes.map((item: string) => {
      data.push(messageTypesDict[item]);
    })
  }

  return data.join();
}

const detail = (row: SubscriptionRecord) => {
  // Normalize fields so el-input/el-checkbox/el-select v-models never see
  // `undefined` — Vue's runtime-dom warns when DOM props receive the
  // wrong type (e.g. `checked` set to undefined instead of a boolean).
  // Older subscription rows may predate one or more of these fields.
  subscription.value = {
    ...row,
    wechatUserId: row.wechatUserId ?? "",
    fromUsers: row.fromUsers ?? [],
    fromGroups: row.fromGroups ?? [],
    keyword: row.keyword ?? "",
    at: typeof row.at === "boolean" ? row.at : !!row.at,
    replyType: typeof row.replyType === "number" ? row.replyType : 0,
    content: row.content ?? "",
    messageTypes: row.messageTypes ?? [],
  };
  detailDialogVisible.value = true;
}

const del = () => {
  ElMessageBox.confirm(
    "确定删除吗？",
    "Warning",
    {
      confirmButtonText: 'OK',
      cancelButtonText: 'Cancel',
      type: 'warning',
    }
  ).then(() => {
    if (!subscription.value) return;
    deleting.value = true;
    deleteSubscription(subscription.value._id).then(res => {
      if (res.successful) {
        ElMessage.success("delete successful");
        loadSubscription();
        detailDialogVisible.value = false;
      } else {
        ElMessage.error(res.message ?? "delete failed");
      }
    }).catch(() => {
      ElMessage.error("delete failed");
    }).finally(() => {
      deleting.value = false;
    });
  }).catch(() => {
    ElMessage({
      type: 'info',
      message: 'Delete canceled',
    })
  });
}

const save = () => {
  if (!subscription.value) return;
  saving.value = true;
  const s = subscription.value;
  saveSubscription(s._id, s.wechatUserId, s.fromUsers, s.fromGroups,
    s.keyword, s.at, s.replyType, s.content, s.messageTypes).then(res => {
      if (res.successful) {
        ElMessage.success("save successful");
        detailDialogVisible.value = false;
        loadSubscription();
      } else {
        ElMessage.error(res.message ?? "save failed");
      }
    }).catch(() => {
      ElMessage.error("save failed");
    }).finally(() => {
      saving.value = false;
    });
}

const addSubscription = () => {
  subscription.value = {
    _id: "",
    wechatUserId: "",
    fromUsers: [],
    fromGroups: [],
    keyword: "",
    at: false,
    replyType: 0,
    content: "",
    messageTypes: [],
  }
  detailDialogVisible.value = true;
}

loadWechatAccountNames();
loadSubscription();
</script>

<style scoped>
.row {
  line-height: 30px;
}

.caption {
  text-align: right;
}

.hint {
  color: #909399;
  font-style: italic;
}
</style>